import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'equifoot.theme';

/**
 * Le thème initial est déjà posé sur <html data-theme> par le script inline de
 * index.html (avant le premier paint, donc pas de flash). Ce service ne fait
 * que refléter cet état et le basculer.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

  readonly theme = signal<Theme>('light');

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    const current = this.document.documentElement.getAttribute('data-theme');
    this.theme.set(current === 'dark' ? 'dark' : 'light');
  }

  toggle(): void {
    this.set(this.theme() === 'dark' ? 'light' : 'dark');
  }

  set(theme: Theme): void {
    this.theme.set(theme);

    if (!isPlatformBrowser(this.platformId)) return;

    this.document.documentElement.setAttribute('data-theme', theme);
    this.document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'dark' ? '#0a0f14' : '#f2f5f7');

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Navigation privée ou stockage refusé: le thème reste valable pour la session.
    }
  }
}
